import { useCallback, useEffect, useId, useRef, useState } from 'react';
import {
  Button,
  Text,
  Tooltip,
  mergeClasses,
} from '@fluentui/react-components';
import {
  ArrowRepeatAllRegular,
  CheckmarkCircleFilled,
  CheckmarkRegular,
  CopyRegular,
  DismissCircleFilled,
} from '@fluentui/react-icons';
import type { RetryAttempt } from '@/types/monitoring';
import { formatDateTime, formatDuration } from '@/utils/dateTime';
import { useRetryHistoryStyles } from './RetryHistory.styles';

type RetryHistoryProps = {
  /** Retry attempts the engine made for this action, in chronological order. */
  attempts: RetryAttempt[];
};

// HTTP 2xx status reason phrases (as the runtime emits them in `code`).
// Retry-history entries are the RETRIED (failed) attempts, and `error` can be
// absent even on a failure, so success is judged from a known success code and
// only falls back to error-absence when no code is present.
const SUCCESS_CODES = new Set([
  'OK',
  'Created',
  'Accepted',
  'NonAuthoritativeInformation',
  'NoContent',
  'ResetContent',
  'PartialContent',
  'MultiStatus',
  'AlreadyReported',
  'IMUsed',
]);

/**
 * Whether an attempt succeeded. Prefer a known success `code`; only fall back to
 * error-absence when `code` is missing, because runtime retry-history entries can
 * omit `error` even for failed attempts.
 */
const attemptSucceeded = (attempt: RetryAttempt): boolean =>
  attempt.code ? SUCCESS_CODES.has(attempt.code) : !attempt.error;

/**
 * Read-only value with a copy button. Kept as a small composition primitive so
 * each field is identical and the copy affordance is consistent.
 */
const CopyableValue = ({
  label,
  value,
  mono,
  multiline,
}: {
  label: string;
  value: string;
  mono?: boolean;
  multiline?: boolean;
}) => {
  const styles = useRetryHistoryStyles();
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>();
  // Namespace the label id per instance so aria-labelledby stays unique even
  // when two RetryHistory panels are mounted at once (panel + expand dialog).
  const baseId = useId();
  const labelId = `${baseId}-${label.replace(/\s+/g, '-').toLowerCase()}`;

  useEffect(() => () => clearTimeout(timer.current), []);

  const handleCopy = useCallback(() => {
    if (!navigator.clipboard?.writeText) return;

    navigator.clipboard
      .writeText(value)
      .then(() => {
        setCopied(true);
        clearTimeout(timer.current);
        timer.current = setTimeout(() => setCopied(false), 1500);
      })
      .catch(() => {
        // Clipboard can reject (permissions, unfocused document); ignore.
      });
  }, [value]);

  return (
    <div className={styles.field}>
      <Text as="span" className={styles.fieldLabel} id={labelId}>
        {label}
      </Text>
      <div className={styles.valueBox}>
        <span
          className={mergeClasses(
            styles.value,
            mono && styles.valueMono,
            multiline && styles.errorValue,
          )}
          aria-labelledby={labelId}
        >
          {value}
        </span>
        <Tooltip content={copied ? 'Copied' : `Copy ${label.toLowerCase()}`} relationship="label">
          <Button
            appearance="subtle"
            size="small"
            icon={copied ? <CheckmarkRegular /> : <CopyRegular />}
            aria-label={`Copy ${label.toLowerCase()}`}
            onClick={handleCopy}
          />
        </Tooltip>
      </div>
    </div>
  );
};

/** The detail field list for a single selected attempt. */
const AttemptFields = ({
  attempt,
  panelId,
  labelledById,
}: {
  attempt: RetryAttempt;
  panelId: string;
  labelledById: string;
}) => {
  const styles = useRetryHistoryStyles();
  const succeeded = attemptSucceeded(attempt);
  const durationText = formatDuration(attempt.durationMs, { style: 'execution', fallback: '—' });
  const statusCode = attempt.code ?? (succeeded ? 'Succeeded' : 'Failed');

  return (
    <div className={styles.fields} role="tabpanel" id={panelId} aria-labelledby={labelledById}>
      <CopyableValue label="Duration" value={durationText} mono />
      <CopyableValue label="Start time" value={formatDateTime(attempt.startTime, 'full', { seconds: true })} />
      {attempt.endTime && (
        <CopyableValue label="End time" value={formatDateTime(attempt.endTime, 'full', { seconds: true })} />
      )}
      <div className={styles.field}>
        <Text as="span" className={styles.fieldLabel}>Status</Text>
        <div className={styles.valueBox}>
          <span
            className={mergeClasses(
              styles.statusValue,
              succeeded ? styles.statusValueSucceeded : styles.statusValueFailed,
            )}
          >
            {succeeded ? (
              <CheckmarkCircleFilled aria-hidden />
            ) : (
              <DismissCircleFilled aria-hidden />
            )}
            {statusCode}
          </span>
        </div>
      </div>
      {attempt.clientRequestId && (
        <CopyableValue label="Client request ID" value={attempt.clientRequestId} mono />
      )}
      {attempt.serviceRequestId && (
        <CopyableValue label="Service request ID" value={attempt.serviceRequestId} mono />
      )}
      {attempt.error?.message && (
        <CopyableValue label="Error" value={attempt.error.message} multiline />
      )}
    </div>
  );
};

/**
 * Retry history for an action — a horizontal rail of selectable attempt cards
 * (status + duration) above a read-only field list for the selected attempt.
 *
 * Mirrors the `IterationSelector` interaction model already used in this panel
 * (roving-tabindex rail, arrow-key navigation) so the two feel like siblings.
 */
export const RetryHistory = ({ attempts }: RetryHistoryProps) => {
  const styles = useRetryHistoryStyles();
  const baseId = useId();
  const panelId = `${baseId}-panel`;
  const railRef = useRef<HTMLDivElement>(null);
  const lastIndex = Math.max(attempts.length - 1, 0);

  // Default to the last attempt: it carries the final outcome users care about.
  const [selectedIndex, setSelectedIndex] = useState(lastIndex);

  // Reset the selection to the last attempt whenever the underlying attempt set
  // changes — e.g. the user selects a different action (this component is reused
  // without a key) or the engine appends a new attempt during a live run.
  // The signature spans every attempt's start time + code (not just first/last)
  // so two distinct actions can't collide on it; it stays stable across ordinary
  // re-renders, preserving the user's chosen attempt while viewing one action.
  // Adjusting state during render (instead of in an effect) avoids an extra
  // commit + flash.
  const signature = `${attempts.length}|${attempts
    .map((a) => `${a.startTime}:${a.code ?? ''}`)
    .join('|')}`;
  const [prevSignature, setPrevSignature] = useState(signature);
  if (signature !== prevSignature) {
    setPrevSignature(signature);
    setSelectedIndex(lastIndex);
  }

  // Keep the selected card in view. Honor reduced-motion. Depends on the
  // signature too so the rail re-scrolls when the action changes even if the
  // reset lands on the same numeric index.
  useEffect(() => {
    const rail = railRef.current;
    const selectedEl = rail?.querySelector<HTMLElement>('[data-retry-selected="true"]');
    const prefersReduced =
      typeof window !== 'undefined' &&
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    selectedEl?.scrollIntoView({
      block: 'nearest',
      inline: 'nearest',
      behavior: prefersReduced ? 'auto' : 'smooth',
    });
  }, [selectedIndex, signature]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      let nextIndex = selectedIndex;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') {
        e.preventDefault();
        nextIndex = Math.min(selectedIndex + 1, attempts.length - 1);
      } else if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') {
        e.preventDefault();
        nextIndex = Math.max(selectedIndex - 1, 0);
      } else if (e.key === 'Home') {
        e.preventDefault();
        nextIndex = 0;
      } else if (e.key === 'End') {
        e.preventDefault();
        nextIndex = attempts.length - 1;
      }
      if (nextIndex !== selectedIndex) {
        setSelectedIndex(nextIndex);
        const tabs = railRef.current?.querySelectorAll<HTMLButtonElement>('[role="tab"]');
        tabs?.[nextIndex]?.focus();
      }
    },
    [selectedIndex, attempts.length],
  );

  if (attempts.length === 0) return null;

  const totalDuration = attempts.reduce((sum, a) => sum + (a.durationMs ?? 0), 0);
  const totalStr = formatDuration(totalDuration, { style: 'execution', fallback: '' });
  const selected = attempts[selectedIndex] ?? attempts[0]!;

  return (
    <div className={styles.root}>
      <div className={styles.railWrap}>
        <div className={styles.head}>
          <span className={styles.headLabel}>
            <ArrowRepeatAllRegular className={styles.headLabelIcon} aria-hidden />
            Retry attempts
          </span>
          <span className={styles.headMeta}>
            {totalStr ? `Total: ${totalStr} · ` : ''}
            {attempts.length} {attempts.length === 1 ? 'attempt' : 'attempts'}
          </span>
        </div>
        <div
          ref={railRef}
          className={styles.rail}
          role="tablist"
          aria-label="Retry attempts"
          aria-orientation="horizontal"
          onKeyDown={handleKeyDown}
        >
          {attempts.map((attempt, i) => {
            const isActive = i === selectedIndex;
            const succeeded = attemptSucceeded(attempt);
            const cardClass = mergeClasses(
              styles.card,
              !isActive && !succeeded && styles.cardFailed,
              isActive && styles.cardSelected,
            );
            const statusClass = mergeClasses(
              styles.cardStatus,
              succeeded ? styles.cardStatusSucceeded : styles.cardStatusFailed,
              isActive && styles.cardStatusOnSelected,
            );
            const durStr = formatDuration(attempt.durationMs, { style: 'execution', fallback: '—' });
            const statusWord = succeeded ? 'succeeded' : 'failed';
            return (
              <button
                key={attempt.startTime}
                type="button"
                role="tab"
                id={`${baseId}-tab-${i}`}
                aria-selected={isActive}
                aria-controls={panelId}
                data-retry-selected={isActive}
                tabIndex={isActive ? 0 : -1}
                className={cardClass}
                aria-label={`Attempt ${i + 1}, ${statusWord}, ${durStr}`}
                onClick={() => setSelectedIndex(i)}
              >
                <span className={styles.cardTop}>
                  <span className={styles.cardIndex}>{i + 1}</span>
                  <span className={statusClass} aria-hidden>
                    {succeeded ? <CheckmarkCircleFilled /> : <DismissCircleFilled />}
                  </span>
                </span>
                <span
                  className={mergeClasses(
                    styles.cardDuration,
                    isActive && styles.cardDurationOnSelected,
                  )}
                >
                  {durStr}
                </span>
              </button>
            );
          })}
        </div>
      </div>
      <AttemptFields attempt={selected} panelId={panelId} labelledById={`${baseId}-tab-${selectedIndex}`} />
    </div>
  );
};
